import React from 'react';

import { useMatchMedia } from '@betterment/js-runtime';
import { Box, Card, H1, Icon, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { CardLoader, SambaAutoLink, useFeatureFlag } from '@betterment/shared-components';

import { addAccountUrl } from '../../Shared/addAccountUrl';
import { urlBuilder } from '../../Shared/urlBuilder';
import { HomeProductOnboardingOptionsQuery } from '../graphql/homeProductOnboardingOptions.generated';
import { HomeProductBundlesQuery } from '../graphql/productBundles.generated';
import { useProductBundles } from '../hooks/useProductBundles';
import { useProductOnboardingOptions } from '../hooks/useProductOnboardingOptions';
import { getBundleResumeUrl } from '../utils/bundleResumeUrl';

type ProductOption = HomeProductOnboardingOptionsQuery['productOnboardingOptions'][number];
type Bundle = HomeProductBundlesQuery['productBundles'][number];

type OptionCardProps = {
  productOption?: ProductOption;
  bundle?: Bundle;
  marginTop?: 'space.200';
};

const OptionCard: React.FC<OptionCardProps> = ({ productOption, bundle, marginTop }) => {
  const isSmallScreen = useMatchMedia('atLeastMedium');

  const { enabled: isSolo401kImprovementsEnabled } = useFeatureFlag({
    splitName: 'retail.solo_401k_improvements_q1_2026_enabled',
    context: 'ProductOnboardingInProgressCard',
  });

  if (!bundle && !productOption) return null;

  const steps = bundle?.steps || productOption?.steps;
  const image = bundle?.image || productOption?.image;
  const name = bundle?.title || productOption?.name;

  const hasSteps = !!steps?.length;
  const flexProps = hasSteps
    ? { flexDirection: 'column', alignItems: 'start', justifyContent: 'start' }
    : { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' };

  const href = () => {
    if (bundle) {
      return getBundleResumeUrl(bundle);
    }

    if (productOption!.__typename === 'PremiumOnboardingOption') {
      return urlBuilder.appAdviceInitiationFlow.absolute();
    }

    if (productOption!.__typename === 'Solo401kOnboardingOption' && !isSolo401kImprovementsEnabled) {
      return urlBuilder.appAdviceInitiationFlow.absolute({
        signup_intent: productOption!.legacyProductCategory,
        product_category: productOption!.legacyProductCategory,
      });
    }

    const params: Record<string, string> = { product_category: productOption!.legacyProductCategory };
    if (productOption!.__typename === 'Solo401kOnboardingOption' && isSolo401kImprovementsEnabled) {
      params.qualified = 'true';
    }
    return addAccountUrl(params);
  };

  const gridColumnStart = isSmallScreen ? 1 : 2;

  return (
    <Card
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: 'space.300',
        marginTop,
        gridColumnStart,
      }}
    >
      <Box css={{ display: 'flex', ...flexProps }}>
        <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
          <img
            className={css({ height: 'icon.medium', width: 'icon.medium', marginRight: 'space.150' })}
            src={image!.light}
            alt={`${name} icon`}
          />
          <H1 variant="title200">{name}</H1>
        </Box>
        {hasSteps && (
          <Box css={{ display: 'flex', flexDirection: 'column', marginY: 'space.300', gap: 'space.100' }}>
            {steps.map(step => (
              <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} key={step.id}>
                <Icon
                  name={step.state === 'FINISHED' ? 'CheckmarkCircleFilled' : 'CheckmarkCircle'}
                  size="small"
                  color={step.state === 'FINISHED' ? 'icon' : 'icon.weak'}
                  css={{ marginRight: 'space.100' }}
                />
                <Text css={{ textDecoration: step.state === 'FINISHED' ? 'line-through' : 'none' }}>
                  {step.shortDescription}
                </Text>
              </Box>
            ))}
          </Box>
        )}

        <SambaAutoLink to={href()} variant="solidPrimary" spacing="default" css={{ alignSelf: 'start' }}>
          Finish setup
        </SambaAutoLink>
      </Box>
    </Card>
  );
};

export const ProductOnboardingInProgressCard: React.FC = () => {
  const { enabled: isMultiAccountSignupEnabled } = useFeatureFlag({
    splitName: 'retail.web_multi_account_signup_q4_2025_enabled',
    context: 'ProductOnboardingInProgressCard',
  });

  const { data: bundlesData } = useProductBundles();
  const { data: productsData } = useProductOnboardingOptions({
    bundles: isMultiAccountSignupEnabled ? bundlesData?.allBundles : [],
  });

  if (!bundlesData || !productsData) return <CardLoader />;

  const inProgressBundle = isMultiAccountSignupEnabled ? bundlesData.inProgressBundle : null;
  const { inProgressOptions } = productsData;
  const hasContent = inProgressBundle || inProgressOptions.length > 0;
  if (!hasContent) return null;

  return (
    <>
      {inProgressBundle && <OptionCard bundle={inProgressBundle} key={inProgressBundle.id} />}

      {inProgressOptions.map((option, i) => (
        <OptionCard
          productOption={option}
          marginTop={i > 0 || inProgressBundle ? 'space.200' : undefined}
          key={option.id}
        />
      ))}
    </>
  );
};
